import { useState } from 'preact/hooks';
import type { VNode } from 'preact';
import './feedback.css';
import {
  FEEDBACK_CATEGORIES,
  MAX_FEEDBACK_TEXT,
  captureScreenshot,
  submitFeedback,
  type FeedbackCategory,
  type FeedbackCollector,
} from '../game/feedback.js';
import type { FrameCapture } from '../render/capture.js';

export interface FeedbackFormProps {
  collector: FeedbackCollector;
  capture: FrameCapture;
  token: string;
  screenshotDefault: boolean;
  /** '' for the free-form terminal; set to seed the text box. */
  seedText: string;
  onDone: (message: string) => void;
  onCancel: () => void;
}

const CATEGORY_BLURB: Record<string, string> = {
  bug: 'something broke',
  balance: 'a fight felt wrong',
  idea: 'something you want',
  performance: 'it ran badly',
  other: 'anything else',
};

/**
 * A capture only resolves when the frame loop drains it. If the loop has stopped (tab
 * backgrounded, renderer lost) the promise would hang and the send button with it, so
 * every screenshot request races a short deadline and sends without the image.
 */
async function screenshotWithin(capture: FrameCapture, ms: number): Promise<string | null> {
  return Promise.race([
    captureScreenshot(capture),
    new Promise<null>((resolve) => {
      window.setTimeout(() => {
        resolve(null);
      }, ms);
    }),
  ]);
}

export function FeedbackForm(props: FeedbackFormProps): VNode {
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [rating, setRating] = useState(3);
  const [text, setText] = useState(props.seedText);
  const [shot, setShot] = useState(props.screenshotDefault);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [bad, setBad] = useState(false);

  const ctx = props.collector.snapshot();
  const over = text.length > MAX_FEEDBACK_TEXT;

  const send = async (): Promise<void> => {
    if (busy || over) return;
    setBusy(true);
    setBad(false);
    setStatus('sending…');
    let screenshot: string | null = null;
    if (shot) {
      setStatus('capturing frame…');
      screenshot = await screenshotWithin(props.capture, 2000);
      if (screenshot === null) setStatus('screenshot unavailable — sending without it');
    }
    const res = await submitFeedback(
      {
        category,
        rating,
        text: text.trim(),
        // Re-snapshot: the fps window and the position moved on while the form was open.
        context: props.collector.snapshot(),
        ...(screenshot === null ? {} : { screenshot }),
      },
      props.token,
    );
    setBusy(false);
    if (res.ok) {
      props.collector.logEvent(`feedback sent (${category})`);
      props.onDone(res.message.length > 0 ? res.message : 'logged — thank you');
    } else {
      setBad(true);
      setStatus(res.message);
    }
  };

  return (
    <div class="fb">
      <div class="fb-row">
        <span class="fb-label">what is this</span>
        <div class="fb-cats">
          {FEEDBACK_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              class={c === category ? 'fb-cat on' : 'fb-cat'}
              title={CATEGORY_BLURB[c] ?? ''}
              onClick={() => {
                setCategory(c);
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div class="fb-row">
        <span class="fb-label">how does the game feel right now</span>
        <div class="fb-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              class={n <= rating ? 'fb-star on' : 'fb-star'}
              aria-label={`${String(n)} of 5`}
              onClick={() => {
                setRating(n);
              }}
            >
              {n <= rating ? '◉' : '○'}
            </button>
          ))}
        </div>
      </div>

      <div class="fb-row">
        <span class="fb-label">tell us</span>
        <textarea
          value={text}
          maxLength={MAX_FEEDBACK_TEXT + 200}
          placeholder="What happened, what you expected, what would be better."
          onInput={(ev) => {
            setText((ev.currentTarget as HTMLTextAreaElement).value);
          }}
        />
        <span class={over ? 'fb-count over' : 'fb-count'}>
          {String(text.length)} / {String(MAX_FEEDBACK_TEXT)}
        </span>
      </div>

      <label class="fb-check">
        <input
          type="checkbox"
          checked={shot}
          onChange={(ev) => {
            setShot((ev.currentTarget as HTMLInputElement).checked);
          }}
        />
        attach a screenshot of what is on screen
      </label>

      <details class="fb-context">
        <summary>context sent with this report</summary>
        <div>
          build {ctx.build} · {ctx.backend} · {ctx.quality} · fps {String(ctx.fpsP50)} p50 /{' '}
          {String(ctx.fpsP1)} p1 · rtt {String(ctx.rtt)} ms
          <br />
          map {ctx.mapId} · {ctx.mode} · {ctx.phase} · at {String(ctx.pos.x)}, {String(ctx.pos.y)},{' '}
          {String(ctx.pos.z)}
          <br />
          gpu {ctx.gpu}
          <br />
          {ctx.events.length > 0
            ? `last events: ${ctx.events.slice(-5).join(' | ')}`
            : 'no events yet'}
        </div>
      </details>

      <div class={bad ? 'fb-status bad' : 'fb-status'}>{status}</div>

      <div class="fb-actions">
        <button
          type="button"
          class="fb-btn"
          disabled={busy}
          onClick={() => {
            props.onCancel();
          }}
        >
          Close
        </button>
        <button
          type="button"
          class="fb-btn primary"
          disabled={busy || over || text.trim().length === 0}
          onClick={() => {
            void send();
          }}
        >
          {busy ? 'Sending' : 'Send'}
        </button>
      </div>
    </div>
  );
}

export interface FeedbackModalProps extends FeedbackFormProps {
  title: string;
  subtitle: string;
}

/** The F8 / pause presentation: the same form inside a dimmed, blurred card. */
export function FeedbackModal(props: FeedbackModalProps): VNode {
  const { title, subtitle, ...form } = props;
  return (
    <div
      class="fb-modal"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) props.onCancel();
      }}
    >
      <div class="fb-card">
        <h2 class="fb-title">{title}</h2>
        <p class="fb-sub">{subtitle}</p>
        <FeedbackForm {...form} />
      </div>
    </div>
  );
}
